import { apiService } from "./api";
import { getApiErrorMessage } from "../lib/apiErrors";
import type {
  RegisterPushSubscriptionRequest,
  PushSubscriptionResponse,
} from "../types/pushNotifications";

class PushNotificationService {
  private vapidPublicKey: string | null = null;
  private readonly DEVICE_ENDPOINT_KEY = 'push_subscription_endpoint';

  /**
   * Store the device's push endpoint in localStorage for tracking subscription state
   */
  private storeDeviceEndpoint(endpoint: string): void {
    try {
      localStorage.setItem(this.DEVICE_ENDPOINT_KEY, endpoint);
    } catch (error) {
      console.warn('Failed to store device endpoint in localStorage:', error);
    }
  }

  /**
   * Clear the stored device endpoint from localStorage
   */
  private clearDeviceEndpoint(): void {
    try {
      localStorage.removeItem(this.DEVICE_ENDPOINT_KEY);
    } catch (error) {
      console.warn('Failed to clear device endpoint from localStorage:', error);
    }
  }

  /**
   * Get the stored device endpoint from localStorage
   */
  private getStoredDeviceEndpoint(): string | null {
    try {
      return localStorage.getItem(this.DEVICE_ENDPOINT_KEY);
    } catch (error) {
      console.warn('Failed to get device endpoint from localStorage:', error);
      return null;
    }
  }

  /**
   * Check if push notifications are supported in the browser
   */
  isSupported(): boolean {
    return (
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window
    );
  }

  /**
   * Get current notification permission state
   */
  getPermissionState(): NotificationPermission {
    if (!this.isSupported()) return "denied";
    return Notification.permission;
  }

  /**
   * Request notification permission from the user
   */
  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported()) {
      throw new Error("Push notifications are not supported");
    }

    const permission = await Notification.requestPermission();
    return permission;
  }

  /**
   * Get VAPID public key from backend
   */
  async getVapidPublicKey(): Promise<string> {
    if (this.vapidPublicKey) return this.vapidPublicKey;

    const response = await apiService.getVapidPublicKey();

    if (!response.success || !response.data) {
      throw new Error(
        getApiErrorMessage(response, "Couldn't load push notification settings from the server")
      );
    }

    this.vapidPublicKey = response.data.publicKey;
    return this.vapidPublicKey;
  }

  /**
   * Subscribe to push notifications
   */
  async subscribe(
    deviceName?: string
  ): Promise<PushSubscriptionResponse | null> {
    if (!this.isSupported()) {
      throw new Error("Push notifications are not supported");
    }

    // Check permission
    if (Notification.permission !== "granted") {
      const permission = await this.requestPermission();
      if (permission !== "granted") {
        return null;
      }
    }

    // Get service worker registration
    const registration = await navigator.serviceWorker.ready;

    // Get VAPID public key
    const vapidPublicKey = await this.getVapidPublicKey();

    // If no VAPID key configured, return null
    if (!vapidPublicKey) {
      console.warn("VAPID public key not configured");
      return null;
    }

    // Convert VAPID key to Uint8Array
    const applicationServerKey = this.urlBase64ToUint8Array(vapidPublicKey);

    // Subscribe to push manager. Its errors are browser-internal DOMExceptions
    // (e.g. "Registration failed - push service error"), so don't pass them to the user as-is.
    let subscription: PushSubscription;
    try {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
    } catch (error) {
      console.error("pushManager.subscribe failed:", error);
      throw new Error(
        "This browser couldn't register for push notifications. Check that notifications are allowed for this site and try again."
      );
    }

    // Send subscription to backend
    const subscriptionJson = subscription.toJSON();
    const request: RegisterPushSubscriptionRequest = {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: subscriptionJson.keys?.p256dh || "",
        auth: subscriptionJson.keys?.auth || "",
      },
      deviceName,
      userAgent: navigator.userAgent,
    };

    const response = await apiService.subscribeToPush(request);

    if (!response.success || !response.data) {
      throw new Error(
        getApiErrorMessage(response, "Failed to register push subscription")
      );
    }

    // Store endpoint for this device to track subscription state
    this.storeDeviceEndpoint(subscription.endpoint);

    return response.data;
  }

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribe(): Promise<boolean> {
    if (!this.isSupported()) return false;

    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      // Clear stored endpoint even if no browser subscription exists
      this.clearDeviceEndpoint();
      return false;
    }

    // Unsubscribe from push manager
    await subscription.unsubscribe();

    // Notify backend
    const response = await apiService.unsubscribeFromPush(subscription.endpoint);

    // Clear stored endpoint regardless of backend response
    this.clearDeviceEndpoint();

    return response.success;
  }

  /**
   * Stop this device receiving the signed-out user's push notifications. Only acts when this device subscribed
   * (a stored endpoint), and gives up after a few seconds so a stuck service worker can't hold up logout.
   */
  async unsubscribeForLogout(timeoutMs = 3000): Promise<void> {
    let hasEndpoint = false;
    try {
      hasEndpoint = !!localStorage.getItem(this.DEVICE_ENDPOINT_KEY);
    } catch {
      // Storage unavailable - nothing recorded to clean up
    }
    if (!hasEndpoint || !this.isSupported()) return;

    try {
      await Promise.race([
        this.unsubscribe(),
        new Promise((resolve) => setTimeout(resolve, timeoutMs)),
      ]);
    } catch (error) {
      console.warn('Failed to unsubscribe from push notifications on logout:', error);
    }
  }

  /**
   * Get current push subscription status
   */
  async getSubscriptionStatus(): Promise<PushSubscription | null> {
    if (!this.isSupported()) return null;

    try {
      const registration = await navigator.serviceWorker.ready;
      return registration.pushManager.getSubscription();
    } catch {
      return null;
    }
  }

  /**
   * Check if currently subscribed to push notifications
   */
  async isSubscribed(): Promise<boolean> {
    const subscription = await this.getSubscriptionStatus();
    return subscription !== null;
  }

  /**
   * Get comprehensive subscription state by reconciling browser and backend state.
   * Handles PWA updates where browser subscription may be lost on THIS device only.
   * Uses localStorage to track device-specific subscription state to prevent
   * cross-device auto-subscription issues.
   */
  async getSubscriptionState(): Promise<{
    isSubscribed: boolean;
    needsResubscription: boolean;
    browserSubscription: PushSubscription | null;
  }> {
    if (!this.isSupported()) {
      return { isSubscribed: false, needsResubscription: false, browserSubscription: null };
    }

    // If permission denied, nothing we can do
    if (Notification.permission === "denied") {
      return { isSubscribed: false, needsResubscription: false, browserSubscription: null };
    }

    try {
      const browserSubscription = await this.getSubscriptionStatus();

      if (browserSubscription) {
        // Browser has subscription - ensure endpoint is stored for this device
        this.storeDeviceEndpoint(browserSubscription.endpoint);
        return { isSubscribed: true, needsResubscription: false, browserSubscription };
      }

      // Browser has no subscription - check if THIS device had one before (PWA update scenario)
      // Only check if permission is granted (user previously allowed notifications)
      if (Notification.permission === "granted") {
        const storedEndpoint = this.getStoredDeviceEndpoint();

        if (storedEndpoint) {
          // This device previously had push enabled - check if backend still has it
          const backendSubscriptions = await this.getSubscriptions();
          const thisDeviceSubscription = backendSubscriptions.find(
            (sub) => sub.endpoint === storedEndpoint
          );

          if (thisDeviceSubscription) {
            // Backend still has THIS device's subscription - browser state was lost
            // Need to re-subscribe to restore push on this device
            return { isSubscribed: false, needsResubscription: true, browserSubscription: null };
          } else {
            // Backend doesn't have this device's subscription anymore
            // User intentionally unsubscribed or subscription expired - clear stored endpoint
            this.clearDeviceEndpoint();
          }
        }
        // No stored endpoint = user never enabled push on this device (or intentionally disabled)
      }

      // No browser subscription and no valid stored endpoint - not subscribed
      return { isSubscribed: false, needsResubscription: false, browserSubscription: null };
    } catch (error) {
      console.error("Error checking subscription state:", error);
      // On error, fall back to browser-only check
      const browserSubscription = await this.getSubscriptionStatus();
      return {
        isSubscribed: browserSubscription !== null,
        needsResubscription: false,
        browserSubscription,
      };
    }
  }

  /**
   * Get all subscriptions for the current user
   */
  async getSubscriptions(): Promise<PushSubscriptionResponse[]> {
    const response = await apiService.getPushSubscriptions();
    return response.data || [];
  }

  /**
   * Convert base64 URL-safe string to Uint8Array (needed for VAPID key)
   */
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, "+")
      .replace(/_/g, "/");

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
}

export const pushNotificationService = new PushNotificationService();
